/* External */
import {
  memo, useEffect, useMemo, useCallback, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';

/* Ott-common */
import CodeError from '@dtvgo/code-error';
import { isWithinIntervalDate } from '@dtvgo/date-utils';

/* Components */
import { FormattedMessage } from 'react-intl';
import Carousel from '../Carousel';
import CardVod from '../CardVod';
import LiveCard from '../LiveCard';
import FocusableButton from '../FocusableButton';
import SwimlaneLiveSkeleton from '../SwimlaneLiveSkeleton';

/* Providers */
import { useUserAccount } from '../../providers/UserProvider';

/* Hooks */
import useFetchMoreLikeThisVod from '../../hooks/useFetchMoreLikeThisVod';
import useFetchMoreLikeThisEpg from '../../hooks/useFetchMoreLikeThisEpg';
import useGetPositionCarousel from '../../hooks/useGetPositionCarousel';
import useHandleThrottleArrowPress from '../../hooks/useHandleThrottleArrowPress';
import useHandleFocusRestoredCarousel from '../../hooks/useHandleFocusRestoredCarousel';
import useFetchClickAction from '../../hooks/useFetchClickAction';

/* Utils */
import { getRem, noOp } from '../../utils/functions';
import { moreLikeThisVodTitle, moreLikeThisEpgTitle } from '../../utils/env';
import { CardType, CardWidth, MaxCardHeight } from '../../utils/cardsUtils';
import { redirectToDetail, redirectToPlayer } from '../../utils/history';
import {
  GTM_EVENT_NAMES,
  GTM_SUBCATEGORIES,
  formatPlaySource,
  transmissionContentType,
} from '../../utils/googleTags';
import { normalizeMoreLikeThisEPG, normalizeMoreLikeThisVOD } from './utils';
import { PLAY_SOURCE_PAGE } from '../../utils/trackSourceConviva';

/* Styles */
import './MoreLikeThisVodEpg.scss';

function MoreLikeThisVodEpg({
  className = '',
  contentId = '',
  pdpCommonTagEvent = noOp,
  isDisplaying = false,
  focusable = true,
  realFocusKey = '',
  setFocus = noOp,
}) {
  const positionsList = useRef();
  const getPositionsList = useCallback(() => {
    if (!positionsList.current) {
      positionsList.current = [];
    }
    return positionsList.current;
  }, []);
  const updatePositionCarousel = useCallback((carouselIndex, newCarouselPosition) => {
    getPositionsList()[carouselIndex] = newCarouselPosition;
  }, [getPositionsList]);
  const [tryFetchAgain, setTryFetchAgain] = useState(false);
  const buttonFocusKey = `${realFocusKey}_button`;

  const {
    msoProvider,
    accountType: userType,
  } = useUserAccount() || {};

  const { fetchClickAction } = useFetchClickAction();

  const {
    moreLikeThisItemsVod,
    moreLikeThisTitleVod,
    isFetchingVod,
    errorVod,
  } = useFetchMoreLikeThisVod({ contentId, tryFetchAgain, isDisplaying });
  const {
    moreLikeThisItemsEpg,
    moreLikeThisTitleEpg,
    isFetchingEpg,
    errorEpg,
  } = useFetchMoreLikeThisEpg({ contentId, tryFetchAgain, isDisplaying });
  const shouldDisplayCarouselVod = !isFetchingVod
    && moreLikeThisItemsVod && moreLikeThisItemsVod.length > 0;
  const shouldDisplayCarouselEpg = !isFetchingEpg
    && moreLikeThisItemsEpg && moreLikeThisItemsEpg.length > 0;

  const shouldDisplayErrorMessage = useMemo(() => {
    const isVODItemsEmpty = moreLikeThisItemsVod.length === 0;
    const isEPGItemsEmpty = moreLikeThisItemsEpg.length === 0;
    const isFetchFinished = !isFetchingVod && !isFetchingEpg;
    const hasItemsEmpty = isVODItemsEmpty && isEPGItemsEmpty;
    const hasError = errorVod && errorEpg;

    return (isFetchFinished && (hasItemsEmpty || hasError));
  }, [
    moreLikeThisItemsVod,
    moreLikeThisItemsEpg,
    errorVod,
    errorEpg,
    isFetchingVod,
    isFetchingEpg,
  ]);

  // VOD and EPG carousel settings
  const carouselVodLength = Math.min(moreLikeThisItemsVod?.length);
  const carouselEpgLength = Math.min(moreLikeThisItemsEpg?.length);
  const onPositionCarouselChange = useCallback((newPositionCarousel) => {
    updatePositionCarousel(0, newPositionCarousel);
  }, [updatePositionCarousel]);

  const [
    positionCarouselVod,
    handleArrowPressCarouselVod,
    setPositionCarouselVod,
  ] = useGetPositionCarousel({
    carouselLength: carouselVodLength,
    onPositionCarouselChange,
  });

  const [
    positionCarouselEpg,
    handleArrowPressCarouselEpg,
    setPositionCarouselEpg,
  ] = useGetPositionCarousel({
    carouselLength: carouselEpgLength,
    onPositionCarouselChange,
  });

  const handleCardArrowPressCarouselVOD = useHandleThrottleArrowPress(handleArrowPressCarouselVod);
  const handleCardFocusRestoredCarouselVOD = useHandleFocusRestoredCarousel(setPositionCarouselVod);

  const handleCardArrowPressCarouselEPG = useHandleThrottleArrowPress(handleArrowPressCarouselEpg);
  const handleCardFocusRestoredCarouselEPG = useHandleFocusRestoredCarousel(setPositionCarouselEpg);

  const { state: useLocationState, pathname } = useLocation();
  const isMyList = className.includes('my-list');
  const isShow = pathname.includes('detail/shows');

  const errorData = {
    sourceError: 'MIDDLEWARE',
    platformError: 'WEBOS',
    locationError: 'PDP',
    typeError: 'UNEXPECTED',
  };

  const locationState = useCallback(({ content }) => ({
    actionOrigin: content?.actionOrigin || '',
    transmissionType: transmissionContentType(content?.type || content?.schema),
    sectionName: isMyList ? 'my-list' : (useLocationState?.carouselTitle || useLocationState?.sectionName),
    componentType: isMyList ? 'my-list' : 'catalog-collection',
    collectionName: '',
    gtmPlaySource: isMyList ? 'my-list' : (formatPlaySource({
      parentLander: useLocationState?.parentLander,
      section: useLocationState?.carouselTitle,
    }) || useLocationState?.gtmPlaySource),
  }), [
    isMyList,
    useLocationState?.carouselTitle,
    useLocationState?.gtmPlaySource,
    useLocationState?.parentLander,
    useLocationState?.sectionName,
  ]);

  const handlePdpCommonTagEvent = useCallback(() => {
    if (!isMyList) {
      pdpCommonTagEvent({
        eventName: GTM_EVENT_NAMES.PDP.WATCH_CONTENT,
        subcategory: isShow
          ? GTM_SUBCATEGORIES.PDP.SERIE_SEASONS_AND_RELATED_CONTENT
          : GTM_SUBCATEGORIES.PDP.MOVIES_RELATED_CONTENT,
      });
    }
  }, [isMyList, isShow, pdpCommonTagEvent]);

  const handleRedirectToPlayer = useCallback(({ content, componentType }) => {
    redirectToPlayer(content, {
      page: PLAY_SOURCE_PAGE.PDP,
      section: content?.title || '',
      state: locationState({ content, componentType }),
    });
  }, [locationState]);

  const buildClickActionParams = useCallback((content) => ({
    actionOrigin: content?.actionOrigin || '',
    userOrigin: {
      playSource: PLAY_SOURCE_PAGE.PDP,
      userLogin: userType || '',
      msoProvider: msoProvider || '',
    },
    contentType: content?.isLive ? 1 : 2,
    guideId: content?.streaming?.id || content?.vrioAssetId || '',
    vrioAssetId: content?.vrioAssetId || '',
  }), [msoProvider, userType]);

  const handleEnterPress = useCallback((content) => {
    if (content?.startTime && content?.endTime) {
      const startDate = content?.startTime
        ? new Date(content?.startTime)
        : undefined;
      const endDate = content?.endTime ? new Date(content?.endTime) : undefined;
      const isLive = isWithinIntervalDate(startDate, endDate);

      if (isLive) {
        handleRedirectToPlayer({ content, componentType: CardType.LIVE });
        return;
      }
    }

    const clickActionParams = buildClickActionParams(content);

    fetchClickAction(clickActionParams);
    redirectToDetail(content, { state: locationState({ content }) });
    handlePdpCommonTagEvent();
  }, [
    locationState,
    handlePdpCommonTagEvent,
    handleRedirectToPlayer,
    fetchClickAction,
    buildClickActionParams,
  ]);

  const cardsVOD = useMemo(() => moreLikeThisItemsVod?.map((item, index) => (
    <CardVod
      key={item.vrioAssetId}
      index={index}
      active={positionCarouselVod === index}
      item={normalizeMoreLikeThisVOD(item)}
      onEnterPress={() => handleEnterPress(item)}
      onArrowPress={handleCardArrowPressCarouselVOD}
      onFocusRestored={handleCardFocusRestoredCarouselVOD}
    />
  )), [
    handleCardArrowPressCarouselVOD,
    handleCardFocusRestoredCarouselVOD,
    handleEnterPress,
    moreLikeThisItemsVod,
    positionCarouselVod,
  ]);

  const cardsEPG = useMemo(() => moreLikeThisItemsEpg?.map((item, index) => (
    <LiveCard
      key={item.vrioAssetId}
      index={index}
      item={normalizeMoreLikeThisEPG(item)}
      onEnterPress={() => handleEnterPress(normalizeMoreLikeThisEPG(item))}
      onArrowPress={handleCardArrowPressCarouselEPG}
      onFocusRestored={handleCardFocusRestoredCarouselEPG}
    />
  )), [
    handleCardArrowPressCarouselEPG,
    handleCardFocusRestoredCarouselEPG,
    handleEnterPress,
    moreLikeThisItemsEpg,
  ]);

  useEffect(() => {
    if (shouldDisplayErrorMessage) {
      setFocus(buttonFocusKey);
    }
  }, [setFocus, buttonFocusKey, shouldDisplayErrorMessage]);

  return (
    <div className="dtv-html5-c-more-like-this-vod-epg">
      {isFetchingVod && (
        <SwimlaneLiveSkeleton />
      )}

      {shouldDisplayCarouselVod && (
        <Carousel
          title={moreLikeThisTitleVod || moreLikeThisVodTitle}
          maxCardWidthRem={getRem(CardWidth[CardType.VOD] + 20)}
          maxCardHeightRem={getRem(MaxCardHeight[CardType.VOD])}
          positionCarousel={positionCarouselVod}
          setPositionCarousel={setPositionCarouselVod}
          isModalShown={false}
          focusable={focusable}
        >
          {cardsVOD}
        </Carousel>
      )}

      {isFetchingEpg && (
        <SwimlaneLiveSkeleton />
      )}

      {shouldDisplayCarouselEpg && (
        <Carousel
          title={moreLikeThisTitleEpg || moreLikeThisEpgTitle}
          maxCardWidthRem={getRem(CardWidth[CardType.LIVE] + 20)}
          maxCardHeightRem={getRem(MaxCardHeight[CardType.LIVE])}
          positionCarousel={positionCarouselEpg}
          setPositionCarousel={setPositionCarouselEpg}
          isModalShown={false}
          focusable={focusable}
        >
          {cardsEPG}
        </Carousel>
      )}

      {shouldDisplayErrorMessage && (
        <div className="dtv-html5-c-more-like-this-vod-epg__empty-message">
          <p className="dtv-html5-c-more-like-this-vod-epg__empty-message__title">
            <FormattedMessage id="moreLikeThis.emptyMessage" />
          </p>
          <div className="dtv-html5-c-more-like-this-vod-epg__empty-message__error-code">
            <p><FormattedMessage id="moreLikeThis.code" /></p>
            <CodeError
              sourceError={errorData.sourceError}
              platformError={errorData.platformError}
              locationError={errorData.locationError}
              typeError={errorData.typeError}
            />
          </div>
          <div>
            <FocusableButton
              className="dtv-html5-c-more-like-this-vod-epg__button"
              onEnterPress={() => setTryFetchAgain((prevState) => !prevState)}
              focusKey={buttonFocusKey}
              icon="refresh"
              primary
              bordered
            >
              <FormattedMessage id="moreLikeThis.buttonTitle" />
            </FocusableButton>
          </div>
        </div>
      )}
    </div>
  );
}

MoreLikeThisVodEpg.propTypes = {
  className: PropTypes.string,
  contentId: PropTypes.string,
  focusable: PropTypes.bool,
  pdpCommonTagEvent: PropTypes.func,
  isDisplaying: PropTypes.bool,
  realFocusKey: PropTypes.string,
  setFocus: PropTypes.func,
};

export default memo(MoreLikeThisVodEpg);
